import {hpcAttrs} from '@github-ui/hpc'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import type {NativeSelectionMap} from '@github-ui/taillights-symbols/native-selection'
import type {SourceRange, SourceSymbol} from '@github-ui/taillights-worker/document'
import {testIdProps} from '@github-ui/test-id-props'
import {CommandEvent, ScopedCommands} from '@github-ui/ui-commands'
import {useIsSafari} from '@github-ui/use-sticky-header/useIsSafari'
import {EllipsisIcon} from '@primer/octicons-react'
import {Banner, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {
  type ComponentProps,
  type KeyboardEvent,
  type Ref,
  use,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'

import {useIsFirefox} from '../../utilities/lines'
import codeLineStyles from '../blob/BlobContent/Code/CodeLine.module.css'
import {ExperimentalSourceOptionsContext} from './experimental-source-options'
import {SourceLabelContext, useCodeBlobStartupError} from './source-context'
import {SourceModelContext, SourceModelProvider, useSourceModelContext} from './source-model'
import {lineRange} from './source-navigation'
import {gutterFocusScrollTop, initialWindowChunks} from './source-window'
import {SourceCaret} from './SourceCaret'
import {TaillightsPreviewControlsContext} from './taillights-preview-controls'
import styles from './TaillightsCodeView.module.css'
import {type TaillightsGutterAction, TaillightsGutterMenu} from './TaillightsGutterRow'
import {TaillightsStickyScrollbar} from './TaillightsStickyScrollbar'
import {useGutterFocus} from './use-gutter-focus'
import type {GutterActions} from './use-gutter-selection'
import {SourceChunks, WindowedSource} from './WindowedSource'

export interface CodeBlobTaillightsProps {
  embedded?: boolean
  foldingAvailable?: boolean
  showFoldButtons?: boolean
  /** Errors keyed by original, 1-based source line. */
  codeownersErrors?: Readonly<Record<number, string>>
  onStickyLineRemoval?: (originalLineNumber: number) => void
  ref?: Ref<HTMLTextAreaElement>
  highlightRanges?: readonly SourceRange[]
  focusedHighlightRange?: SourceRange | null
  onSymbolSelected?: (symbol: SourceSymbol) => void
  gutterActions?: GutterActions
  commands?: ComponentProps<typeof ScopedCommands>['commands']
  onLineMount?: (line: number, element: HTMLAnchorElement) => void | (() => void)
}

const noActions: readonly TaillightsGutterAction[] = []
const ssrTextareaLineLimit = 1000

/** Unwrapped source viewer. Its CodeBlobTaillightsProvider must be owned by the caller. */
export function CodeBlobTaillights({foldingAvailable, onStickyLineRemoval, ...props}: CodeBlobTaillightsProps) {
  const sharedModel = use(SourceModelContext)
  if (sharedModel) return <SourceContent {...props} />
  return (
    <SourceModelProvider foldingAvailable={foldingAvailable} onStickyLineRemoval={onStickyLineRemoval}>
      <SourceContent {...props} />
    </SourceModelProvider>
  )
}

function SourceContent({
  embedded = false,
  ref,
  codeownersErrors,
  highlightRanges: controlledRanges,
  focusedHighlightRange,
  onSymbolSelected: reportSymbol,
  gutterActions,
  commands,
  onLineMount,
  showFoldButtons = true,
}: Omit<CodeBlobTaillightsProps, 'foldingAvailable' | 'onStickyLineRemoval'>) {
  const model = useSourceModelContext()
  const {
    navigation,
    materializeAllLines,
    virtualized,
    reverseSticky,
    linesToBuffer,
    linesPerChunk,
    scrollPaddingTop,
    scrollSurfaceRef,
    stickyOffsetRef,
    stickyWindowRef,
    sourcePlaneRef,
    reverseStickyRefs,
    fallbackText,
    renderError,
    onRenderError,
    lineHighlight,
    anchorHighlight,
    highlightRanges: localRanges,
    focusedResult: localFocusedResult,
    activeRange,
    highlightLines,
    gutterSelection,
    onSymbolSelected: inspectLocalSymbol,
  } = model
  const {
    lines,
    sections,
    foldingEnabled,
    foldingReady,
    symbolsRef,
    textareaRef,
    viewportRef,
    documentRef,
    scrollMargin,
    collapsed,
    foldMarkers,
    visible,
    view,
    textProjection,
    nativeView,
    nativeTextProjection,
    lineHeight,
    error,
    onSelectionChange,
    anchorRange,
    toggleFold,
    expandFold,
  } = navigation
  const {isBrowser} = useRuntimeEnvironment()
  // A virtualized server render paints only its first chunks; the native text below them stays readable until
  // hydration, when the client's own chunks take over and the text turns transparent again.
  const serverPaintedHeight =
    !isBrowser && virtualized ? Math.min(lines.length, initialWindowChunks * linesPerChunk) * lineHeight : undefined
  const nativeProjection = useMemo<NativeSelectionMap>(() => {
    if (isBrowser || nativeTextProjection.lines.length <= ssrTextareaLineLimit) return nativeTextProjection
    const nativeLines = nativeTextProjection.lines.slice(0, ssrTextareaLineLimit)
    const lineNumbers = nativeTextProjection.lineNumbers.slice(0, ssrTextareaLineLimit)
    return {
      lines: nativeLines,
      text: nativeLines.join('\n'),
      lineNumbers,
      lineStarts: nativeTextProjection.lineStarts.slice(0, ssrTextareaLineLimit),
      sourceToDisplay: new Map(lineNumbers.map((line, index) => [line, index])),
    }
  }, [isBrowser, nativeTextProjection])
  const hydrationSelectionRef = useRef<Pick<
    HTMLTextAreaElement,
    'selectionStart' | 'selectionEnd' | 'selectionDirection'
  > | null>(null)
  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    if (nativeProjection !== nativeTextProjection) {
      hydrationSelectionRef.current = {
        selectionStart: textarea.selectionStart,
        selectionEnd: textarea.selectionEnd,
        selectionDirection: textarea.selectionDirection,
      }
    } else if (hydrationSelectionRef.current) {
      // Extending a controlled textarea value moves its selection to the end.
      const {selectionStart, selectionEnd, selectionDirection} = hydrationSelectionRef.current
      hydrationSelectionRef.current = null
      textarea.setSelectionRange(selectionStart, selectionEnd, selectionDirection)
    }
  }, [nativeProjection, nativeTextProjection, textareaRef])
  const highlightRanges = controlledRanges ?? localRanges
  const focusedResult = focusedHighlightRange === undefined ? localFocusedResult : focusedHighlightRange
  const onSymbolSelected = reportSymbol ?? inspectLocalSymbol
  const options = use(ExperimentalSourceOptionsContext)
  const isFirefox = useIsFirefox()
  const isSafari = useIsSafari()
  const controls = use(TaillightsPreviewControlsContext)
  const startupError = useCodeBlobStartupError()
  const sourceLabel = use(SourceLabelContext)
  const nativeRef = useCallback(
    (node: HTMLTextAreaElement) => {
      textareaRef.current = node
      const cleanup = typeof ref === 'function' ? ref(node) : undefined
      if (ref && typeof ref !== 'function') ref.current = node
      return () => {
        textareaRef.current = null
        if (typeof cleanup === 'function') cleanup()
        else if (typeof ref === 'function') ref(null)
        else if (ref) ref.current = null
      }
    },
    [textareaRef, ref],
  )
  const showLineActions = gutterActions !== undefined || (controls?.showLineActions ?? false)
  const lineActionsRef = useCallback(
    (node: HTMLDivElement) => {
      const viewport = viewportRef.current
      if (!viewport) return
      const syncScroll = () => {
        const offset = `${-viewport.scrollLeft}px`
        if (node.style.getPropertyValue('--source-scroll-offset') === offset) return
        node.style.setProperty('--source-scroll-offset', offset)
      }
      syncScroll()
      // eslint-disable-next-line github/prefer-observers -- Synchronize a scroll offset, not element size or visibility.
      viewport.addEventListener('scroll', syncScroll)
      return () => viewport.removeEventListener('scroll', syncScroll)
    },
    [viewportRef],
  )
  const {ref: gutterRef, focus: gutterFocus, handlers: gutterFocusHandlers} = useGutterFocus()
  const {request: focusRequest} = gutterFocus
  const handledRequestRef = useRef(focusRequest)
  useLayoutEffect(() => {
    if (!focusRequest || handledRequestRef.current === focusRequest) return
    handledRequestRef.current = focusRequest
    if (embedded) viewportRef.current?.scrollTo({left: 0, behavior: 'instant'})
    if (reverseSticky) return
    const row = textProjection.sourceToDisplay.get(focusRequest.line - 1)
    if (row === undefined) return
    const top = gutterFocusScrollTop(
      scrollMargin + row * lineHeight,
      lineHeight,
      scrollPaddingTop,
      window.scrollY,
      window.innerHeight,
    )
    if (top !== window.scrollY) window.scrollTo({top, behavior: 'instant'})
  }, [
    embedded,
    viewportRef,
    reverseSticky,
    focusRequest,
    handledRequestRef,
    textProjection,
    lineHeight,
    scrollMargin,
    scrollPaddingTop,
  ])
  const actions = useMemo<GutterActions>(
    () => gutterActions ?? controls?.gutterActions ?? {...gutterSelection, actions: noActions},
    [gutterActions, controls?.gutterActions, gutterSelection],
  )
  const sourceMenuOwner = actions.owner?.kind === 'menu' && actions.owner.origin === 'source' ? actions.owner : null
  useLayoutEffect(() => {
    // Source shortcuts focus the portalled menu, not its potentially clipped gutter anchor.
    if (embedded && sourceMenuOwner) viewportRef.current?.scrollTo({left: 0, behavior: 'instant'})
  }, [embedded, sourceMenuOwner, viewportRef])
  const rowActions = useMemo(() => (embedded ? {...actions, actions: noActions} : actions), [embedded, actions])
  // Like the production viewer, a highlighted range offers its actions on its first line.
  const selectedMenuLine =
    actions.owner?.kind === 'menu' ? actions.owner.lineNumber : anchorRange ? anchorRange.start.line + 1 : null
  const menuLine = actions.actionLine === undefined ? selectedMenuLine : actions.actionLine
  const menuRow = menuLine == null ? undefined : textProjection.sourceToDisplay.get(menuLine - 1)
  const menuStyle = {
    top: (menuRow ?? 0) * lineHeight,
    height: lineHeight,
  }
  const foldStarts = useMemo(
    () => new Set(foldingEnabled && showFoldButtons ? sections.map(section => section.startLine) : []),
    [foldingEnabled, showFoldButtons, sections],
  )
  const gutter = useMemo(
    () => ({
      visible,
      foldStarts,
      foldingReady,
      collapsed,
      anchorRange,
      onToggle: toggleFold,
      actions: rowActions,
      lines,
      codeownersErrors,
      showLineActions,
      embedded,
      onLineMount,
    }),
    [
      visible,
      foldStarts,
      foldingReady,
      collapsed,
      anchorRange,
      toggleFold,
      rowActions,
      lines,
      codeownersErrors,
      showLineActions,
      embedded,
      onLineMount,
    ],
  )
  const openMenu = {...controls?.commands, ...commands}['code-view:open-line-menu']
  const onSourceKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.defaultPrevented || !openMenu || !actions.actions.length || !visible.length) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Native menu keys supplement the existing open-line-menu command; caret/page keys remain native.
      if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) {
        openMenu(new CommandEvent('code-view:open-line-menu'))
        event.preventDefault()
      }
    },
    [openMenu, actions.actions.length, visible.length],
  )
  const sourceHeight = Math.max(lineHeight, view?.height ?? lines.length * lineHeight)
  return (
    <>
      {renderError || error || startupError ? (
        <Banner variant="critical">
          <Banner.Title>Syntax rendering failed</Banner.Title>
          <Banner.Description>{renderError || error || startupError} Native text remains available.</Banner.Description>
        </Banner>
      ) : null}
      <div
        {...gutterFocusHandlers}
        ref={gutterRef}
        {...hpcAttrs({enabled: embedded})}
        {...testIdProps('taillights-source-container')}
        className={clsx(styles.SourceContainer, embedded && styles.embedded)}
      >
        <div
          {...testIdProps('taillights-scroll-surface')}
          className={clsx(
            styles.ScrollSurface,
            embedded && styles.embedded,
            !isFirefox && !isSafari && styles.layerSkirts,
          )}
          ref={scrollSurfaceRef}
          data-reverse-sticky={reverseSticky || undefined}
          style={{
            height: reverseSticky
              ? embedded
                ? sourceHeight
                : `calc(${sourceHeight}px + var(--borderWidth-thin) * 2)`
              : undefined,
          }}
        >
          <div className={styles.StickyOffset} ref={stickyOffsetRef} aria-hidden />
          <div className={styles.StickyWindow} ref={stickyWindowRef}>
            <div
              {...testIdProps('taillights-viewport')}
              className={clsx(styles.Viewport, embedded && styles.embedded, isBrowser && styles.stickyScrollbarSource)}
              ref={viewportRef}
            >
              <div
                className={clsx(styles.Document, !view && styles.unprepared)}
                ref={documentRef}
                data-line-actions={showLineActions}
                data-blob-layout={embedded || undefined}
                style={{
                  width:
                    reverseSticky && view
                      ? `calc(var(--gutter-width) + ${view.width}px + 2 * var(--source-column-padding))`
                      : undefined,
                  height: !view && !reverseSticky ? sourceHeight : undefined,
                }}
              >
                <SourceChunks
                  key={`${linesToBuffer ?? 'chunks'}:${virtualized}:${reverseSticky}`}
                  view={view}
                  projection={textProjection}
                  linesPerChunk={linesPerChunk}
                  renderer={isFirefox ? 'css' : (options.renderer ?? 'bitmap')}
                  safariCSSHighlighting={options.safariCSSHighlighting ?? false}
                  linesToBuffer={linesToBuffer}
                  preparedFor={navigation.preparedFor}
                  virtualized={virtualized}
                  reverseStickyRefs={reverseSticky ? reverseStickyRefs : undefined}
                  gutter={gutter}
                  focus={gutterFocus}
                  scrollMargin={scrollMargin}
                  scrollPaddingTop={scrollPaddingTop}
                  onRenderError={onRenderError}
                  plaintext={materializeAllLines}
                />
                <ScopedCommands
                  as="div"
                  className={clsx(styles.Source, reverseSticky && styles.windowLocalSource)}
                  commands={{
                    ...controls?.commands,
                    ...commands,
                    'code-view:inspect-symbol': () => symbolsRef.current?.inspectSelection(),
                    'code-view:highlight-line': () => highlightLines(lineRange(lines, activeRange().start.line)),
                  }}
                >
                  <WindowedSource
                    preparedFor={navigation.preparedFor}
                    sourceLineCount={lines.length}
                    sourcePlaneRef={sourcePlaneRef}
                    ref={symbolsRef}
                    textareaRef={nativeRef}
                    aria-label={sourceLabel}
                    // The server-rendered textarea keeps its native caret until the drawn one can replace it.
                    hideNativeCaret={isBrowser}
                    plainTextFrom={serverPaintedHeight}
                    view={nativeView}
                    decorationView={view}
                    initialProjection={nativeProjection}
                    hasPlaintextPaint
                    fallbackText={fallbackText}
                    onSelectionChange={onSelectionChange}
                    onRenderError={onRenderError}
                    onSymbolSelected={onSymbolSelected}
                    highlightRanges={highlightRanges}
                    focusedHighlightRange={focusedResult}
                    anchorHighlightRange={anchorHighlight}
                    onKeyDown={showLineActions && view ? onSourceKeyDown : undefined}
                  >
                    <SourceCaret textareaRef={textareaRef} projection={nativeProjection} />
                    {lineHighlight && (
                      <div
                        {...testIdProps('taillights-line-highlight')}
                        aria-hidden
                        className={styles.LineHighlight}
                        style={lineHighlight}
                      />
                    )}
                    {embedded &&
                      foldMarkers
                        .filter(
                          ({top}) =>
                            !lineHighlight ||
                            top < lineHighlight.top ||
                            top >= lineHighlight.top + lineHighlight.height,
                        )
                        .map(({lineNumber, top, height}) => (
                          <div
                            key={lineNumber}
                            {...testIdProps('taillights-fold-highlight')}
                            aria-hidden
                            className={clsx(
                              codeLineStyles.highlighterElement,
                              codeLineStyles.highlighterElementSubtle,
                              styles.FoldHighlight,
                            )}
                            style={{top, height}}
                          />
                        ))}
                    {foldMarkers.length > 0 && (
                      <div className={styles.FoldMarkers}>
                        {foldMarkers.map(({lineNumber, left, top, height}) => {
                          const button = (
                            <IconButton
                              key={lineNumber}
                              className={styles.FoldMarker}
                              icon={EllipsisIcon}
                              variant="invisible"
                              size="small"
                              aria-label={`Expand row ${lineNumber}`}
                              aria-expanded={false}
                              style={{left: left ?? undefined, top: left === null ? undefined : top, height}}
                              onMouseDown={event => event.preventDefault()}
                              onClick={event => expandFold(lineNumber, event.detail === 0)}
                            />
                          )
                          return left === null ? (
                            <span key={lineNumber} className={styles.NativeFoldMarker} style={{top, height}}>
                              <span aria-hidden className={styles.FoldMarkerText}>
                                {lines[lineNumber - 1]}
                              </span>
                              {button}
                            </span>
                          ) : (
                            button
                          )
                        })}
                      </div>
                    )}
                  </WindowedSource>
                </ScopedCommands>
              </div>
            </div>
          </div>
        </div>
        {/* Without script the code keeps its own scrollbar, as the sticky bar needs a script to follow it. */}
        {isBrowser && !materializeAllLines && <TaillightsStickyScrollbar viewportRef={viewportRef} />}
        {embedded && showLineActions && menuLine != null && menuRow !== undefined && actions.actions.length > 0 && (
          <div
            ref={lineActionsRef}
            className={styles.LineActionsLayer}
            style={{'--line-number-digits': String(menuLine).length}}
          >
            <div className={styles.LineActionRow} style={menuStyle}>
              <TaillightsGutterMenu
                key={menuLine}
                embedded
                lineNumber={menuLine}
                lane={0}
                tabStop
                menuOpen={actions.owner?.kind === 'menu' && actions.owner.lineNumber === menuLine}
                actions={actions.actions}
                menuLabel={actions.menuLabel}
                onMenuOpenChange={actions.onMenuOpenChange}
                controlProps={{
                  'data-gutter-line': menuLine,
                  onPointerDownCapture: actions.captureSnapshot,
                  onKeyDownCapture: actions.captureSnapshot,
                  onFocus: () => gutterFocus.onFocusChange(menuLine),
                  onBlur: event => {
                    if (!event.currentTarget.contains(event.relatedTarget)) gutterFocus.onFocusChange(null)
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}
